import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Grid,
  HStack,
  Heading,
  Image,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  Wrap,
  WrapItem,
  useToast,
} from '@chakra-ui/react';
import { useStores } from '../store/store_context';
import {
  getMonitoringAudit,
  getMonitoringCommandHistory,
  getMonitoringDevice,
  getMonitoringDevices,
  runMonitoringCommand,
} from '../store/api_funcs';
import { base_url } from '../store/base_values';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const fmt = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ru-RU');
};

const toLocalInput = value => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const metric = value => (value === null || value === undefined ? '—' : `${Number(value).toFixed(1)}%`);
const temp = value => (value === null || value === undefined ? '—' : `${Number(value).toFixed(1)}°C`);
const imageSrc = url => (url ? `${base_url.replace(/\/$/, '')}${url}` : '');
const HIDDEN_COMMANDS = new Set(['reboot_panel', 'restart_agent', 'restart_remote_console', 'restart_remote_agent']);
const PERIOD_COLORS = ['#8B5CF6', '#06B6D4', '#10B981', '#F59E0B', '#EF4444'];
const PANEL_FILTER_OPTIONS = [
  { value: 'all', label: 'Все панели' },
  { value: 'online', label: 'Онлайн' },
  { value: 'offline', label: 'Офлайн' },
  { value: 'errors', label: 'С ошибками' },
  { value: 'hot', label: 'Температура 75+' },
];

const CustomSelect = ({ value, options, onChange, maxW = '220px' }) => {
  const selected = options.find(option => option.value === value) || options[0];

  return (
    <Menu placement="bottom-start" matchWidth>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon boxSize={5} />}
        w="100%"
        maxW={maxW}
        h="40px"
        px={3}
        flexShrink={0}
        bg="rgba(255,255,255,0.04)"
        border="1px solid rgba(255,255,255,0.38)"
        borderRadius="6px"
        color="white"
        fontWeight="400"
        textAlign="left"
        _hover={{ bg: 'rgba(255,255,255,0.09)', borderColor: 'rgba(255,255,255,0.58)' }}
        _active={{ bg: 'rgba(255,255,255,0.11)' }}
        _expanded={{ bg: 'rgba(255,255,255,0.11)', borderColor: '#8B5CF6' }}
        _focusVisible={{ boxShadow: '0 0 0 2px rgba(139,92,246,0.7)' }}
      >
        {selected?.label || 'Выберите значение'}
      </MenuButton>
      <MenuList
        minW="190px"
        p={1}
        zIndex={1800}
        bg="#171827"
        border="1px solid rgba(255,255,255,0.18)"
        borderRadius="7px"
        boxShadow="0 16px 40px rgba(0,0,0,0.48)"
      >
        {options.map(option => {
          const isSelected = option.value === value;
          return (
            <MenuItem
              key={option.value}
              onClick={() => onChange(option.value)}
              bg={isSelected ? 'rgba(139,92,246,0.28)' : 'transparent'}
              color="white"
              borderRadius="5px"
              fontWeight={isSelected ? '700' : '400'}
              _hover={{ bg: isSelected ? 'rgba(139,92,246,0.36)' : 'rgba(255,255,255,0.1)' }}
              _focus={{ bg: isSelected ? 'rgba(139,92,246,0.36)' : 'rgba(255,255,255,0.1)' }}
            >
              <HStack justify="space-between" w="100%">
                <Text>{option.label}</Text>
                {isSelected ? <Text color="#B9A4FF">✓</Text> : null}
              </HStack>
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
};

const panelName = device => device?.name || device?.panel?.comment || device?.panel?.adress || '—';

const pickInitialFabric = list => {
  const online = list.find(item => item.online && item.fabric_number && !['-', '---'].includes(item.fabric_number));
  const real = list.find(item => item.fabric_number && !['-', '---'].includes(item.fabric_number));
  return (online || real || list[0])?.fabric_number || null;
};

const AuthorizedImage = ({ url, token, onReady, ...props }) => {
  const [src, setSrc] = useState('');

  useEffect(() => {
    let objectUrl = '';
    if (!url || !token) {
      setSrc('');
      return undefined;
    }
    fetch(imageSrc(url), { headers: { Authorization: `Bearer ${token}` } })
      .then(response => (response.ok ? response.blob() : Promise.reject(new Error('image load failed'))))
      .then(blob => {
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
        if (onReady) onReady(objectUrl);
      })
      .catch(() => setSrc(''));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, token, onReady]);

  return src ? (
    <Image src={src} {...props} />
  ) : (
    <Box bg="rgba(255,255,255,0.08)" border="1px solid rgba(255,255,255,0.12)" borderRadius="6px" {...props} />
  );
};

const MetricTile = ({ label, value, compact = false }) => (
  <Stat bg="rgba(255,255,255,0.06)" border="1px solid rgba(255,255,255,0.12)" borderRadius="8px" p={compact ? 2 : 3} minW={0}>
    <StatLabel color="rgba(255,255,255,0.64)" fontSize={compact ? '11px' : '13px'}>{label}</StatLabel>
    <StatNumber color="white" fontSize={compact ? '18px' : '24px'} fontWeight="700" whiteSpace="nowrap">{value}</StatNumber>
  </Stat>
);

const getNightText = value => {
  if (!value || !value.enabled) return 'Выключен';
  return `${value.start || '—'} - ${value.end || '—'} (${value.active ? 'активен сейчас' : 'не активен'})`;
};

const InfoRow = ({ label, value }) => (
  <HStack justify="space-between" gap={4} borderBottom="1px solid rgba(255,255,255,0.08)" py={2}>
    <Text color="rgba(255,255,255,0.58)" fontSize="13px">{label}</Text>
    <Text color="white" textAlign="right" fontSize="13px" fontWeight="600" whiteSpace="normal" wordBreak="break-word">{value || '—'}</Text>
  </HStack>
);

const SectionCard = ({ title, children, actions, ...props }) => (
  <Box border="1px solid rgba(255,255,255,0.14)" borderRadius="8px" p={3} bg="rgba(255,255,255,0.045)" minW={0} {...props}>
    <HStack justify="space-between" align="center" mb={title ? 2 : 0}>
      {title ? <Heading size="sm" color="white" fontSize="16px">{title}</Heading> : <Box />}
      {actions}
    </HStack>
    {children}
  </Box>
);

const DetailsBlock = ({ title, children, defaultOpen = false, badge }) => (
  <Box
    as="details"
    open={defaultOpen}
    sx={{
      '& > summary': {
        cursor: 'pointer',
        listStyle: 'none',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        color: 'white',
        fontWeight: 700,
        fontSize: '14px',
      },
      '& > summary::-webkit-details-marker': { display: 'none' },
    }}
  >
    <Box as="summary">
      <Text>{title}</Text>
      {badge}
    </Box>
    <Box mt={3}>{children}</Box>
  </Box>
);

const normalizeSchedule = device => {
  const raw = device?.theme_schedule || device?.themeSchedule || device?.active_theme_schedule?.schedule || [];
  return Array.isArray(raw) ? raw : [];
};

const normalizeCommandHistory = value => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.commands)) return value.commands;
  if (Array.isArray(value?.history)) return value.history;
  return [];
};

const commandOutput = item => {
  if (!item) return '—';
  const value =
    item.stdout ??
    item.stderr ??
    item.output ??
    item.result_text ??
    item.response ??
    item.result?.output ??
    item.error ??
    item.payload;
  if (!value) return 'Ответ пока не получен';
  return typeof value === 'string' ? value : JSON.stringify(value, null, 2);
};

const extractManualScreenshot = output => {
  const match = String(output || '').match(/manual_screenshot=([^\s]+)/);
  return match?.[1] || '';
};

const humanCommandOutput = item => {
  if (!item) return 'Ответ пока не получен';
  const output = commandOutput(item);
  const type = item.type;
  const status = commandStatus(item);
  if (['queued', 'running', ''].includes(status)) return 'Команда ожидает выполнения на панели.';
  if (type === 'screenshot' || item.label === 'Сделать скриншот') {
    const path = extractManualScreenshot(output);
    if (path) return `Скриншот создан на панели: ${path}`;
    if (/screenshot_failed/i.test(output)) return 'Не удалось сделать скриншот на панели. Проверьте, запущена ли графическая сессия.';
  }
  if (type === 'retry_content') {
    return commandFailed(item)
      ? 'Обновление контента завершилось с ошибкой. Откройте технический ответ для деталей.'
      : 'Команда обновления контента выполнена.';
  }
  if (type === 'restart_player') {
    return commandFailed(item)
      ? 'Перезапуск плеера завершился с ошибкой.'
      : 'Команда перезапуска плеера выполнена.';
  }
  if (type === 'get_logs') {
    return commandFailed(item) ? 'Логи не получены.' : 'Логи получены. Полный текст доступен ниже.';
  }
  if (type === 'check_status' || item.label === 'Проверить статус') {
    if (commandFailed(item)) return 'Проверка статуса завершилась с ошибкой.';
    const lines = output.split('\n').map(line => line.trim()).filter(Boolean);
    const hostname = lines.find((line, index) => index > 0 && !line.includes(':') && !line.includes('load average')) || '';
    const uptime = lines.find(line => /load average|средняя нагрузка/i.test(line)) || '';
    const disk = lines.find(line => /^\/dev\//.test(line) || /\s\/$/.test(line)) || '';
    const mem = lines.find(line => /^(Mem|Память)/i.test(line)) || '';
    return [
      hostname ? `Хост: ${hostname}` : '',
      uptime ? `Нагрузка: ${uptime.replace(/^.*load average:?\s*/i, '')}` : '',
      disk ? `Диск: ${disk}` : '',
      mem ? `Память: ${mem}` : '',
    ].filter(Boolean).join('\n') || 'Статус получен, полный ответ доступен ниже.';
  }
  return commandFailed(item) ? 'Команда завершилась с ошибкой.' : shortOutput(item, 360);
};

const commandStatus = item => String(item?.status || item?.state || item?.result_status || '').toLowerCase();
const commandFinished = item => ['finished', 'success', 'done'].includes(commandStatus(item));
const commandFailed = item => {
  const status = commandStatus(item);
  const output = commandOutput(item).toLowerCase();
  return (
    ['failed', 'error', 'cancelled'].some(value => status.includes(value)) ||
    Number(item?.exit_code || 0) !== 0 ||
    /error|failed|traceback|not found|no such|permission denied|ошиб/i.test(output)
  );
};

const commandColor = item => {
  if (!item) return 'gray';
  if (commandFailed(item)) return 'red';
  if (commandFinished(item)) return 'green';
  return 'purple';
};

const shortOutput = (item, max = 220) => {
  const value = commandOutput(item).replace(/\s+/g, ' ').trim();
  return value.length > max ? `${value.slice(0, max)}...` : value;
};

const actionLabel = action => ({
  'monitoring.device.view': 'Просмотр карточки панели',
  'monitoring.command.queued': 'Команда отправлена на панель',
  'monitoring.command.failed': 'Команда не отправлена',
  'monitoring.command.denied': 'Команда отклонена',
}[action] || action || 'Действие');

const resultLabel = result => ({
  success: 'успешно',
  failed: 'ошибка',
  error: 'ошибка',
}[result] || result || '—');

const lieLevel = item => {
  const raw = String(
    item?.lie_level ||
      item?.lieLevel ||
      item?.deception_level ||
      item?.deceptionLevel ||
      item?.truth_level ||
      item?.truthLevel ||
      item?.level ||
      item?.result?.lie_level ||
      item?.result?.lieLevel ||
      ''
  ).toLowerCase();
  if (['high', 'medium', 'low'].includes(raw)) return raw;
  const score = Number(
    item?.lie_score ??
      item?.lieScore ??
      item?.deception_score ??
      item?.deceptionScore ??
      item?.score ??
      item?.result?.lie_score ??
      item?.result?.lieScore
  );
  if (Number.isFinite(score)) {
    const normalized = score > 1 ? score / 100 : score;
    if (normalized >= 0.67) return 'high';
    if (normalized >= 0.34) return 'medium';
  }
  return 'low';
};

const lieLevelView = item => ({
  high: { label: 'High', color: '#FF7A60', bg: '#FFE8E8' },
  medium: { label: 'Medium', color: '#F6A609', bg: '#FFF4D8' },
  low: { label: 'Low', color: '#2F80ED', bg: '#E7F0FF' },
}[lieLevel(item)] || { label: 'Low', color: '#2F80ED', bg: '#E7F0FF' });

const LieLevelBadge = ({ item }) => {
  const view = lieLevelView(item);
  return (
    <HStack
      as="span"
      title="Уровень лжи"
      justify="center"
      align="center"
      spacing="6px"
      px="12px"
      py="4px"
      minW="71px"
      h="29px"
      borderRadius="24px"
      bg={view.bg}
      flexShrink={0}
    >
      <Box w="8px" h="8px" borderRadius="50%" bg={view.color} />
      <Text color={view.color} fontWeight="700" fontSize="15px" lineHeight="140%">
        {view.label}
      </Text>
    </HStack>
  );
};

const checkQuestionsText = item => {
  const count = Number(
    item?.questions_count ??
      item?.questionsCount ??
      item?.question_count ??
      item?.questionCount ??
      item?.answers_count ??
      item?.answersCount ??
      (Array.isArray(item?.questions) ? item.questions.length : undefined)
  );
  if (Number.isFinite(count) && count > 0) {
    const suffix = count % 10 === 1 && count % 100 !== 11
      ? 'вопрос'
      : count % 10 >= 2 && count % 10 <= 4 && ![12, 13, 14].includes(count % 100)
        ? 'вопроса'
        : 'вопросов';
    return `${count} ${suffix}`;
  }
  return `${actionLabel(item.action)} · ${resultLabel(item.result)}`;
};

const AuditHistoryCard = ({ item }) => (
  <HStack
    align="center"
    spacing="16px"
    p="16px"
    minH="80px"
    bg="#FFFFFF"
    color="#1C1C1C"
    borderRadius="24px"
    boxShadow="0px 2px 4px rgba(93, 93, 93, 0.08)"
  >
    <Box
      w="48px"
      h="48px"
      borderRadius="14px"
      bg="linear-gradient(135deg, #8B5CF6 0%, #06B6D4 100%)"
      color="white"
      display="flex"
      alignItems="center"
      justifyContent="center"
      fontWeight="800"
      fontSize="17px"
      flexShrink={0}
    >
      {(item.username || '—').trim().slice(0, 1).toUpperCase() || '—'}
    </Box>
    <Box flex="1" minW={0}>
      <Text color="#1C1C1C" fontWeight="700" fontSize="17px" lineHeight="140%" noOfLines={1}>
        {fmt(item.created_at)}
      </Text>
      <Text color="rgba(28,28,28,0.6)" fontWeight="400" fontSize="15px" lineHeight="140%" noOfLines={1}>
        {checkQuestionsText(item)}
      </Text>
    </Box>
    <LieLevelBadge item={item} />
  </HStack>
);

const firstFinite = (...values) => {
  for (const value of values) {
    const normalized = typeof value === 'string'
      ? value.replace(',', '.').replace(/[^\d.+-]/g, '')
      : value;
    const numeric = Number(normalized);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
};

const deepValue = (source, paths) => {
  for (const path of paths) {
    const value = String(path)
      .split('.')
      .reduce((current, key) => (current === null || current === undefined ? undefined : current[key]), source);
    if (value !== null && value !== undefined && value !== '') return value;
  }
  return undefined;
};

const metricRaw = (row, names) => {
  const containers = ['', 'metrics', 'metric', 'telemetry', 'system', 'status', 'stats', 'payload', 'data', 'hardware_info'];
  const paths = containers.flatMap(prefix => names.map(name => (prefix ? `${prefix}.${name}` : name)));
  return deepValue(row, paths);
};

const telemetryValue = (row, key) => {
  if (!row) return null;
  if (key === 'memory_usage') {
    const direct = firstFinite(metricRaw(row, [
      'memory_usage',
      'ram_usage',
      'ram_percent',
      'memory_percent',
      'mem_usage',
      'memory',
      'ram',
      'ОЗУ',
      'озу',
    ]));
    if (direct !== null) return direct;
    const total = Number(metricRaw(row, ['memory_total_gb', 'ram_total_gb', 'mem_total_gb', 'memory_total', 'ram_total']));
    const free = Number(metricRaw(row, ['memory_free_gb', 'ram_free_gb', 'mem_free_gb', 'memory_free', 'ram_free']));
    if (total > 0 && free >= 0) return ((total - free) / total) * 100;
    return null;
  }
  if (key === 'cpu_load') {
    return firstFinite(metricRaw(row, ['cpu_load', 'cpu', 'cpu_usage', 'cpu_percent', 'cpu_load_percent', 'processor_load']));
  }
  if (key === 'temperature') {
    return firstFinite(metricRaw(row, ['temperature', 'cpu_temp', 'temp', 'cpu_temperature', 'temperature_c', 'cpu_temperature_c']));
  }
  if (key === 'disk_usage') {
    return firstFinite(metricRaw(row, ['disk_usage', 'disk', 'disk_percent', 'disk_usage_percent', 'storage_usage', 'storage_percent']));
  }
  return row[key] === null || row[key] === undefined ? null : Number(row[key]);
};

const METRIC_OPTIONS = [
  { key: 'cpu_load', label: 'CPU', unit: '%', color: '#8B5CF6', fill: 'rgba(139,92,246,0.14)' },
  { key: 'temperature', label: 'Температура', unit: '°C', color: '#F97316', fill: 'rgba(249,115,22,0.12)' },
  { key: 'disk_usage', label: 'Диск', unit: '%', color: '#06B6D4', fill: 'rgba(6,182,212,0.12)' },
  { key: 'memory_usage', label: 'ОЗУ', unit: '%', color: '#10B981', fill: 'rgba(16,185,129,0.12)' },
];

const MetricsChart = ({ telemetry, metricKeys }) => {
  const selected = METRIC_OPTIONS.filter(item => metricKeys.includes(item.key));
  const rows = (telemetry || []).slice(-120);
  const datasets = selected
    .map(option => {
      const data = rows.map(row => {
        const value = telemetryValue(row, option.key);
        return Number.isFinite(value) ? Number(value.toFixed(2)) : null;
      });
      return data.some(value => value !== null)
        ? {
          label: option.label,
          data,
          borderColor: option.color,
          backgroundColor: option.fill,
          pointRadius: 0,
          pointHoverRadius: 4,
          tension: 0.32,
          fill: false,
          spanGaps: true,
        }
        : null;
    })
    .filter(Boolean);
  if (!rows.length || !datasets.length) {
    return <Text color="rgba(255,255,255,0.58)" fontSize="13px">Нет данных для выбранных показателей.</Text>;
  }
  const data = {
    labels: rows.map(row => {
      const rawTime = row.timestamp || row.created_at || row.time;
      const date = new Date(rawTime);
      return Number.isNaN(date.getTime()) ? rawTime : date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    }),
    datasets,
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, labels: { color: 'rgba(255,255,255,0.76)', boxWidth: 10, boxHeight: 10 } },
      tooltip: {
        callbacks: {
          label: context => {
            const option = METRIC_OPTIONS.find(item => item.label === context.dataset.label);
            return `${context.dataset.label}: ${context.parsed.y}${option?.unit || ''}`;
          },
        },
      },
    },
    scales: {
      x: { ticks: { color: 'rgba(255,255,255,0.55)', maxTicksLimit: 8 }, grid: { color: 'rgba(255,255,255,0.06)' } },
      y: { ticks: { color: 'rgba(255,255,255,0.55)' }, grid: { color: 'rgba(255,255,255,0.08)' } },
    },
  };
  return <Box h="220px"><Line data={data} options={options} /></Box>;
};

const colorToCss = value => {
  if (typeof value === 'number') return `#${value.toString(16).padStart(6, '0').slice(-6)}`;
  if (typeof value === 'string') return value;
  return '#14141f';
};

const themeBackground = theme => {
  const bg = theme?.background || theme?.backgroundData || {};
  const gradientData = bg.constructorData?.data || bg.data || {};
  if (Array.isArray(gradientData.colorStops) && gradientData.colorStops.length) {
    const stops = gradientData.colorStops
      .slice()
      .sort((a, b) => Number(a.offset || 0) - Number(b.offset || 0))
      .map(stop => `${colorToCss(stop.color)} ${Math.round(Number(stop.offset || 0) * 100)}%`);
    return `linear-gradient(${Number(gradientData.angle || 135)}deg, ${stops.join(', ')})`;
  }
  if (bg.type === 'gradient' && Array.isArray(bg.colors) && bg.colors.length) {
    return `linear-gradient(135deg, ${bg.colors.map(colorToCss).join(', ')})`;
  }
  if (bg.gradient?.colors?.length) {
    return `linear-gradient(135deg, ${bg.gradient.colors.map(colorToCss).join(', ')})`;
  }
  if (bg.type === 'image' || bg.type === 'video' || bg.type === 'video-texture') {
    return 'linear-gradient(135deg, rgba(17,24,39,.92), rgba(30,41,59,.78))';
  }
  return colorToCss(bg.color ?? theme?.backgroundColor ?? '#14141f');
};

const SceneMiniPreview = ({ theme, onClick, height = '132px' }) => {
  const widgets = Array.isArray(theme?.widgets) ? theme.widgets.slice(0, 16) : [];
  const width = Number(theme?.display?.width || theme?.width || theme?.size?.width || 540);
  const sceneHeight = Number(theme?.display?.height || theme?.height || theme?.size?.height || 1920);
  return (
    <Box
      position="relative"
      h={height}
      borderRadius="8px"
      overflow="hidden"
      border="1px solid rgba(255,255,255,0.16)"
      bg={themeBackground(theme)}
      cursor={theme ? 'zoom-in' : 'default'}
      onClick={onClick}
    >
      {widgets.map((widget, index) => (
        <Box
          key={`${widget.w || widget.type || 'w'}-${index}`}
          position="absolute"
          left={`${Math.max(0, (Number(widget.x) || 0) / width) * 100}%`}
          top={`${Math.max(0, (Number(widget.y) || 0) / sceneHeight) * 100}%`}
          w={`${Math.max(4, ((Number(widget.size?.width || widget.width) || 80) / width) * 100)}%`}
          h={`${Math.max(2, ((Number(widget.size?.height || widget.height) || 80) / sceneHeight) * 100)}%`}
          borderRadius={`${Math.min(10, Number(widget.cornerRadius || 4))}px`}
          bg={colorToCss(widget.bgColor ?? '#ffffff')}
          opacity={Math.max(0.18, Math.min(1, Number(widget.bgAlpha ?? 0.55)))}
          border="1px solid rgba(255,255,255,0.3)"
          boxShadow="0 2px 8px rgba(0,0,0,.18)"
        />
      ))}
    </Box>
  );
};

const ThemeScheduleCard = ({ entry, index, onPreview }) => {
  const color = PERIOD_COLORS[index % PERIOD_COLORS.length];
  return (
    <Box border={`1px solid ${color}`} borderLeftWidth="5px" borderRadius="10px" p={3} bg={`${color}22`}>
      <HStack justify="space-between" align="center" mb={2}>
        <HStack>
          <Box w="26px" h="26px" borderRadius="999px" bg={color} color="white" display="grid" placeItems="center" fontWeight="800">
            {index + 1}
          </Box>
          <Box>
            <Text color="white" fontWeight="700" fontSize="13px">{entry.title || entry.name || `Тема ${index + 1}`}</Text>
            <Text color="rgba(255,255,255,0.62)" fontSize="12px">{entry.start || '—'} - {entry.end || '—'}</Text>
          </Box>
        </HStack>
        <Badge bg={entry.theme ? 'rgba(16,185,129,.22)' : 'rgba(245,158,11,.22)'} color={entry.theme ? '#86efac' : '#fde68a'}>
          {entry.theme ? 'сохранена' : 'нет темы'}
        </Badge>
      </HStack>
      <SceneMiniPreview theme={entry.theme} onClick={() => entry.theme && onPreview(entry)} />
    </Box>
  );
};

const MonitoringPage = () => {
  const { pageStore } = useStores();
  const toast = useToast();
  const [devices, setDevices] = useState([]);
  const [selectedFabric, setSelectedFabric] = useState(null);
  const [card, setCard] = useState(null);
  const [audit, setAudit] = useState([]);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [cardLoading, setCardLoading] = useState(false);
  const [commandLoading, setCommandLoading] = useState(null);
  const [previewSrc, setPreviewSrc] = useState('');
  const [themePreview, setThemePreview] = useState(null);
  const [commandHistory, setCommandHistory] = useState([]);
  const [metricKeys, setMetricKeys] = useState(['cpu_load', 'memory_usage']);
  const [screenshotModalIndex, setScreenshotModalIndex] = useState(null);
  const [selectedShot, setSelectedShot] = useState(null);
  const [historyFrom, setHistoryFrom] = useState(() => toLocalInput(new Date(Date.now() - 24 * 60 * 60 * 1000)));
  const [historyTo, setHistoryTo] = useState(() => toLocalInput(new Date()));
  const [showCommandTechnical, setShowCommandTechnical] = useState(false);
  const [showCommandScreenshots, setShowCommandScreenshots] = useState(false);
  const selectedFabricRef = useRef(selectedFabric);
  const cardRequestRef = useRef(0);
  const commandHistoryRequestRef = useRef(0);
  const detailCacheRef = useRef(new Map());
  const prefetchingRef = useRef(new Set());
  const canControl = !!pageStore.monitoring_control;

  useEffect(() => {
    selectedFabricRef.current = selectedFabric;
  }, [selectedFabric]);

  const historyRange = useMemo(
    () => ({ from_at: historyFrom || undefined, to_at: historyTo || undefined }),
    [historyFrom, historyTo],
  );

  const cacheKeyFor = fabric => `${fabric || ''}|${historyRange.from_at || ''}|${historyRange.to_at || ''}`;
  const applyDetailBundle = bundle => {
    if (!bundle) return;
    setCard(bundle.card);
    setAudit(bundle.audit || []);
    setCommandHistory(bundle.commandHistory || []);
  };

  const loadCommandHistory = async fabric => {
    if (!fabric) return [];
    const requestId = ++commandHistoryRequestRef.current;
    const historyData = await getMonitoringCommandHistory(fabric, pageStore.token).catch(() => []);
    const history = normalizeCommandHistory(historyData);
    if (requestId === commandHistoryRequestRef.current && selectedFabricRef.current === fabric) {
      setCommandHistory(history);
    }
    return history;
  };

  const loadDevices = async silent => {
    if (!silent) setLoading(true);
    try {
      const data = await getMonitoringDevices(pageStore.token);
      const list = data.devices || [];
      setDevices(list);
      setSelectedFabric(current => {
        const selected = current ? list.find(item => item.fabric_number === current) : null;
        if (selected) {
          setSelectedShot(selected.last_screenshot_url ? { url: selected.last_screenshot_url, timestamp: selected.last_screenshot_at || selected.last_seen } : null);
          return current;
        }
        return pickInitialFabric(list);
      });
      if (!silent) {
        list
          .filter(item => item.fabric_number && !['-', '---'].includes(item.fabric_number))
          .slice(0, 4)
          .forEach((item, index) => setTimeout(() => prefetchCard(item.fabric_number), 500 + index * 900));
      }
    } catch (error) {
      toast({ title: 'Мониторинг недоступен', description: error.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const loadCard = async (fabric, silent = false, options = {}) => {
    if (!fabric) return;
    const requestId = ++cardRequestRef.current;
    const key = cacheKeyFor(fabric);
    const cached = detailCacheRef.current.get(key);
    const isCurrentRequest = () => requestId === cardRequestRef.current && selectedFabricRef.current === fabric;

    if (cached && !options.force) {
      applyDetailBundle(cached);
      if (!silent) setCardLoading(false);
      if (Date.now() - cached.loadedAt < 60000) return;
    } else if (!silent) {
      setCardLoading(true);
    }

    try {
      const data = await getMonitoringDevice(fabric, pageStore.token, historyRange);
      if (!isCurrentRequest()) return;
      setCard(data);
      const [auditData, historyData] = await Promise.all([
        getMonitoringAudit(pageStore.token, fabric, historyRange),
        getMonitoringCommandHistory(fabric, pageStore.token).catch(() => []),
      ]);
      if (!isCurrentRequest()) return;
      const bundle = {
        card: data,
        audit: auditData.items || [],
        commandHistory: normalizeCommandHistory(historyData),
        loadedAt: Date.now(),
      };
      detailCacheRef.current.set(key, bundle);
      applyDetailBundle(bundle);
    } catch (error) {
      if (!silent && isCurrentRequest()) {
        toast({ title: 'Карточка не загружена', description: error.message, status: 'error', duration: 4000, isClosable: true });
      }
    } finally {
      if (!silent && isCurrentRequest()) setCardLoading(false);
    }
  };

  const prefetchCard = async fabric => {
    if (!fabric) return;
    const key = cacheKeyFor(fabric);
    if (detailCacheRef.current.has(key) || prefetchingRef.current.has(key)) return;
    prefetchingRef.current.add(key);
    try {
      const [data, auditData, historyData] = await Promise.all([
        getMonitoringDevice(fabric, pageStore.token, historyRange),
        getMonitoringAudit(pageStore.token, fabric, historyRange).catch(() => ({ items: [] })),
        getMonitoringCommandHistory(fabric, pageStore.token).catch(() => []),
      ]);
      detailCacheRef.current.set(key, {
        card: data,
        audit: auditData.items || [],
        commandHistory: normalizeCommandHistory(historyData),
        loadedAt: Date.now(),
      });
    } catch (error) {
      // Background warmup is best-effort; visible loading still handles errors.
    } finally {
      prefetchingRef.current.delete(key);
    }
  };

  useEffect(() => {
    loadDevices(false);
    const timer = setInterval(() => loadDevices(true), 15000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadCard(selectedFabric);
  }, [selectedFabric, historyFrom, historyTo]);

  useEffect(() => {
    if (!selectedFabric) return undefined;
    const timer = setInterval(() => loadCard(selectedFabric, true), 15000);
    return () => clearInterval(timer);
  }, [selectedFabric, historyFrom, historyTo]);

  useEffect(() => {
    if (!selectedFabric) return undefined;
    const timer = setInterval(() => loadCommandHistory(selectedFabric), 5000);
    return () => clearInterval(timer);
  }, [selectedFabric]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return devices.filter(device => {
      if (filter === 'online' && !device.online) return false;
      if (filter === 'offline' && device.online) return false;
      if (filter === 'errors' && !device.last_error) return false;
      if (filter === 'hot' && !(Number(telemetryValue(device, 'temperature')) >= 75)) return false;
      if (!q) return true;
      return [panelName(device), device.fabric_number, device.panel?.adress, device.panel?.comment, String(device.panel?.panel_id || '')]
        .filter(Boolean)
        .some(value => String(value).toLowerCase().includes(q));
    });
  }, [devices, filter, query]);

  const selectedDevice = card?.device;
  const currentMetric = (key) => {
    const fromDevice = telemetryValue(selectedDevice, key);
    if (fromDevice !== null) return fromDevice;
    const lastRow = (card?.telemetry || []).slice(-1)[0];
    return telemetryValue(lastRow, key);
  };
  const latestShot = selectedShot?.url || selectedDevice?.last_screenshot_url;
  const schedule = normalizeSchedule(selectedDevice);
  const visibleCommands = (card?.commands || []).filter(command => !HIDDEN_COMMANDS.has(command.type));
  const latestStatusCommand = commandHistory.find(item => item.type === 'check_status' || item.label === 'Проверить статус');
  const latestScreenshotCommand = commandHistory.find(item => item.type === 'screenshot' || item.label === 'Сделать скриншот');
  const screenshotHistory = card?.screenshots || [];
  const selectedShotTime = selectedShot?.timestamp || selectedDevice?.last_screenshot_at;
  const filteredScreenshots = useMemo(() => {
    return screenshotHistory;
  }, [screenshotHistory]);
  const selectDevice = device => {
    if (!device?.fabric_number) return;
    const sameDevice = selectedFabricRef.current === device.fabric_number;
    setSelectedFabric(device.fabric_number);
    setSelectedShot(device.last_screenshot_url ? { url: device.last_screenshot_url, timestamp: device.last_screenshot_at || device.last_seen } : null);
    setCard(current => ({
      device,
      telemetry: [],
      screenshots: device.last_screenshot_url
        ? [{ id: 'latest', timestamp: device.last_screenshot_at || device.last_seen, url: device.last_screenshot_url }]
        : [],
      errors: [],
      logs: [],
      commands: current?.commands || [],
    }));
    setAudit([]);
    setCommandHistory([]);
    setPreviewSrc('');
    setScreenshotModalIndex(null);
    setCardLoading(true);
    if (sameDevice) loadCard(device.fabric_number, false, { force: true });
  };
  const openScreenshotModal = index => {
    setScreenshotModalIndex(index);
    setPreviewSrc('');
  };
  const closeScreenshotModal = () => {
    setScreenshotModalIndex(null);
    setPreviewSrc('');
  };

  const runCommand = async type => {
    if (!selectedFabric) return;
    const fabric = selectedFabric;
    const payload = { type };
    setCommandLoading(type);
    try {
      const result = await runMonitoringCommand(fabric, payload, pageStore.token);
      if (selectedFabricRef.current !== fabric) return;
      toast({ title: 'Команда поставлена в очередь', description: `ID ${result.id || '—'}`, status: 'success', duration: 3500, isClosable: true });
      setCommandHistory(current => [result, ...current.filter(item => item.id !== result.id)]);
      await loadCard(fabric, true, { force: true });
      for (let attempt = 0; attempt < 12; attempt += 1) {
        await new Promise(resolve => setTimeout(resolve, 2500));
        if (selectedFabricRef.current !== fabric) break;
        const history = await loadCommandHistory(fabric);
        const current = history.find(item => String(item.id) === String(result.id)) || history[0];
        if (current && current.status && !['queued', 'running'].includes(String(current.status).toLowerCase())) break;
        if (current?.output) break;
      }
      if (selectedFabricRef.current === fabric && ['check_status', 'screenshot', 'retry_content'].includes(type)) {
        await loadCard(fabric, true, { force: true });
      }
    } catch (error) {
      toast({ title: 'Команда не выполнена', description: error.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setCommandLoading(null);
    }
  };

  if (!pageStore.remote_control_panel || !pageStore.monitoring_view) {
    return (
      <Alert status="error" borderRadius="8px">
        <AlertIcon />
        Нет доступа к мониторингу.
      </Alert>
    );
  }

  return (
    <VStack
      align="stretch"
      spacing={3}
      width="100%"
      maxW="100%"
      overflowX="hidden"
      color="white"
      sx={{
        'th': { fontSize: '10px', py: 2, letterSpacing: '0.04em' },
        'td': { fontSize: '12px', py: 2 },
        'button': { minH: '30px', fontSize: '12px' },
        'input, select': { minH: '30px', height: '30px', fontSize: '12px' },
        '.chakra-heading': { letterSpacing: 0 },
      }}
    >
      <HStack justify="space-between" align="flex-start" spacing={3}>
        <Box>
          <Heading size="md" color="white">Мониторинг панелей</Heading>
          <Text color="rgba(255,255,255,0.58)" fontSize="12px">Состояние, скриншоты, метрики и действия по выбранной панели.</Text>
        </Box>
        <Button size="sm" onClick={() => loadDevices(false)} isLoading={loading} bg="#7A4CE0" color="white" _hover={{ bg: '#8B5CF6' }}>
          Обновить
        </Button>
      </HStack>

      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
        <MetricTile compact label="Всего" value={devices.length} />
        <MetricTile compact label="Онлайн" value={devices.filter(d => d.online).length} />
        <MetricTile compact label="Офлайн" value={devices.filter(d => !d.online).length} />
        <MetricTile compact label="Температура 75+" value={devices.filter(d => Number(telemetryValue(d, 'temperature')) >= 75).length} />
      </SimpleGrid>

      <HStack spacing={2} align="center" wrap="wrap">
        <Input
          placeholder="Поиск по номеру, объекту, адресу"
          value={query}
          onChange={e => setQuery(e.target.value)}
          maxW="340px"
          bg="rgba(255,255,255,0.04)"
          borderColor="rgba(255,255,255,0.38)"
          _placeholder={{ color: 'rgba(255,255,255,0.38)' }}
        />
        <CustomSelect
          value={filter}
          options={PANEL_FILTER_OPTIONS}
          onChange={setFilter}
          maxW="190px"
        />
        <Text color="rgba(255,255,255,0.58)" fontSize="12px">История:</Text>
        <Input
          type="datetime-local"
          value={historyFrom}
          onChange={event => setHistoryFrom(event.target.value)}
          maxW="178px"
          bg="rgba(255,255,255,0.04)"
          borderColor="rgba(255,255,255,0.24)"
          color="white"
        />
        <Input
          type="datetime-local"
          value={historyTo}
          onChange={event => setHistoryTo(event.target.value)}
          maxW="178px"
          bg="rgba(255,255,255,0.04)"
          borderColor="rgba(255,255,255,0.24)"
          color="white"
        />
        <Button
          size="xs"
          bg="rgba(255,255,255,0.1)"
          color="white"
          _hover={{ bg: 'rgba(255,255,255,0.16)' }}
          onClick={() => {
            setHistoryFrom(toLocalInput(new Date(Date.now() - 45 * 24 * 60 * 60 * 1000)));
            setHistoryTo(toLocalInput(new Date()));
          }}
        >
          45 дней
        </Button>
        <Button
          size="xs"
          bg="rgba(255,255,255,0.1)"
          color="white"
          _hover={{ bg: 'rgba(255,255,255,0.16)' }}
          onClick={() => {
            setHistoryFrom(toLocalInput(new Date(Date.now() - 24 * 60 * 60 * 1000)));
            setHistoryTo(toLocalInput(new Date()));
          }}
        >
          24 часа
        </Button>
      </HStack>

      <Grid templateColumns={{ base: '1fr', xl: 'minmax(0, 1fr) minmax(330px, 390px)' }} gap={3} alignItems="start" maxW="100%">
        <Box overflowX="auto" border="1px solid rgba(255,255,255,0.18)" borderRadius="8px" bg="rgba(15,15,28,0.86)">
          <Table size="sm" variant="unstyled" sx={{ tableLayout: 'fixed' }}>
            <Thead>
              <Tr borderBottom="1px solid rgba(255,255,255,0.14)">
                <Th color="rgba(255,255,255,0.66)">Панель</Th>
                <Th color="rgba(255,255,255,0.66)">Статус</Th>
                <Th color="rgba(255,255,255,0.66)">CPU</Th>
                <Th color="rgba(255,255,255,0.66)">Темп.</Th>
                <Th color="rgba(255,255,255,0.66)">Диск</Th>
                <Th color="rgba(255,255,255,0.66)">Последний контакт</Th>
                <Th color="rgba(255,255,255,0.66)">Скрин</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filtered.map(device => {
                const selected = selectedFabric === device.fabric_number;
                return (
                  <Tr
                    key={device.fabric_number}
                    cursor="pointer"
                    bg={selected ? 'rgba(122,76,224,0.28)' : 'transparent'}
                    borderBottom="1px solid rgba(255,255,255,0.08)"
                    _hover={{ bg: selected ? 'rgba(122,76,224,0.32)' : 'rgba(255,255,255,0.06)' }}
                    onClick={() => selectDevice(device)}
                  >
                    <Td><Text fontWeight="700" noOfLines={2} fontSize="12px">{panelName(device)}</Text><Text color="rgba(255,255,255,0.5)" fontSize="11px">{device.fabric_number}</Text></Td>
                    <Td><Badge colorScheme={device.online ? 'green' : 'red'}>{device.online ? 'ONLINE' : 'OFFLINE'}</Badge></Td>
                    <Td>{metric(telemetryValue(device, 'cpu_load'))}</Td>
                    <Td>{temp(telemetryValue(device, 'temperature'))}</Td>
                    <Td>{metric(telemetryValue(device, 'disk_usage'))}</Td>
                    <Td>{fmt(device.last_seen)}</Td>
                    <Td>
                      {device.last_screenshot_url ? (
                        <Box onClick={e => e.stopPropagation()}>
                          <AuthorizedImage
                            url={device.last_screenshot_url}
                            token={pageStore.token}
                            w="54px"
                            h="76px"
                            objectFit="cover"
                            borderRadius="6px"
                            cursor="pointer"
                            onClick={() => selectDevice(device)}
                          />
                        </Box>
                      ) : '—'}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>

        <Box border="1px solid rgba(255,255,255,0.28)" borderRadius="8px" p={2} minH="360px" maxH={{ xl: 'calc(100vh - 210px)' }} overflowY="auto" overflowX="hidden" bg="rgba(15,15,28,0.88)">
          {cardLoading && !selectedDevice ? <Spinner /> : selectedDevice ? (
            <Grid templateColumns={{ base: '1fr', lg: '155px minmax(0, 1fr)' }} gap={3} alignItems="start">
              <VStack align="stretch" spacing={3}>
                {latestShot ? (
                  <Box>
                    <AuthorizedImage
                      url={latestShot}
                      token={pageStore.token}
                      w="100%"
                      maxH="420px"
                      objectFit="contain"
                      borderRadius="8px"
                      border="1px solid rgba(255,255,255,0.16)"
                      cursor="zoom-in"
                      bg="rgba(0,0,0,0.18)"
                      onClick={event => setPreviewSrc(event.currentTarget.src)}
                    />
                    <Button mt={2} w="100%" size="xs" onClick={() => setPreviewSrc('pending')} bg="rgba(255,255,255,0.1)" color="white" _hover={{ bg: 'rgba(255,255,255,0.16)' }}>
                      Открыть скрин
                    </Button>
                    {previewSrc === 'pending' ? <AuthorizedImage url={latestShot} token={pageStore.token} display="none" onReady={setPreviewSrc} /> : null}
                  </Box>
                ) : <Alert status="warning" bg="rgba(255,196,87,0.22)" color="white"><AlertIcon />Скриншота нет</Alert>}
                <SimpleGrid columns={1} spacing={2}>
                  {visibleCommands.map(command => (
                    <Button
                      key={command.type}
                      size="sm"
                      bg="#7A4CE0"
                      color="white"
                      border="0"
                      _hover={{ bg: '#8B5CF6' }}
                      onClick={() => runCommand(command.type)}
                      isLoading={commandLoading === command.type}
                      isDisabled={!canControl}
                      whiteSpace="normal"
                      minH="36px"
                    >
                      {command.label}
                    </Button>
                  ))}
                </SimpleGrid>
              </VStack>

              <VStack align="stretch" spacing={3} minW={0}>
                <Box>
                  <Heading size="sm" color="white" noOfLines={2}>{panelName(selectedDevice)}</Heading>
                  <Text color="rgba(255,255,255,0.5)" fontSize="12px">{selectedDevice.fabric_number} · Panel ID {selectedDevice.panel?.panel_id || '—'}</Text>
                </Box>
                <SimpleGrid columns={3} spacing={2}>
                  <MetricTile compact label="CPU" value={metric(currentMetric('cpu_load'))} />
                  <MetricTile compact label="Темп." value={temp(currentMetric('temperature'))} />
                  <MetricTile compact label="Диск" value={metric(currentMetric('disk_usage'))} />
                </SimpleGrid>
                <SectionCard>
                  <HStack justify="space-between" mb={2}>
                    <Text color="white" fontWeight="700" fontSize="14px">Проверка статуса</Text>
                    <Badge colorScheme={commandColor(latestStatusCommand)}>
                      {!latestStatusCommand ? 'нет данных' : commandFailed(latestStatusCommand) ? 'ошибка' : commandFinished(latestStatusCommand) ? 'ок' : latestStatusCommand.status || 'в очереди'}
                    </Badge>
                  </HStack>
                  <Text color="rgba(255,255,255,0.55)" fontSize="12px" mb={2}>
                    {latestStatusCommand ? fmt(latestStatusCommand.finished_at || latestStatusCommand.updated_at || latestStatusCommand.created_at) : 'Команда еще не запускалась'}
                  </Text>
                  <Text color="rgba(255,255,255,0.72)" fontSize="12px" noOfLines={2}>
                    {latestStatusCommand ? humanCommandOutput(latestStatusCommand) : 'Нажмите «Проверить статус», чтобы получить ответ с панели.'}
                  </Text>
                  {latestStatusCommand ? (
                    <DetailsBlock title="Технический ответ">
                      <Box as="pre" whiteSpace="pre-wrap" color="rgba(255,255,255,0.78)" fontSize="12px" maxH="220px" overflowY="auto" m={0}>
                        {commandOutput(latestStatusCommand).slice(0, 5000)}
                      </Box>
                    </DetailsBlock>
                  ) : null}
                </SectionCard>
                <SectionCard>
                  <InfoRow label="Последний контакт" value={fmt(selectedDevice.last_seen)} />
                  <InfoRow label="Последний скриншот" value={fmt(selectedShotTime)} />
                </SectionCard>
                <SectionCard>
                  <DetailsBlock title="Ночной режим" defaultOpen>
                    <InfoRow label="Статус" value={getNightText(selectedDevice.night_mode)} />
                    <InfoRow label="Часовой пояс" value={selectedDevice.night_mode?.timezone || 'Europe/Moscow'} />
                  </DetailsBlock>
                </SectionCard>
                <SectionCard>
                  <DetailsBlock title="Расписание тем" badge={<Badge bg="rgba(122,76,224,0.22)" color="#ddd6fe">{schedule.length}</Badge>}>
                    {schedule.length ? (
                      <VStack align="stretch" spacing={3}>
                        {schedule.map((entry, index) => (
                          <ThemeScheduleCard
                            key={`${entry.start}-${entry.end}-${index}`}
                            entry={entry}
                            index={index}
                            onPreview={setThemePreview}
                          />
                        ))}
                      </VStack>
                    ) : (
                      <Text color="rgba(255,255,255,0.58)" fontSize="13px">Не задано, используется основная тема панели.</Text>
                    )}
                  </DetailsBlock>
                </SectionCard>
                <SectionCard>
                  <DetailsBlock title="Характеристики микрокомпьютера" defaultOpen>
                    <InfoRow label="ОС" value={selectedDevice.hardware_info?.os} />
                    <InfoRow label="Kernel" value={selectedDevice.hardware_info?.kernel} />
                    <InfoRow label="CPU" value={selectedDevice.hardware_info?.cpu} />
                    <InfoRow label="Ядра" value={selectedDevice.hardware_info?.cpu_cores} />
                    <InfoRow label="RAM всего" value={selectedDevice.hardware_info?.memory_total_gb ? `${selectedDevice.hardware_info.memory_total_gb} ГБ` : '—'} />
                    <InfoRow label="RAM свободно" value={selectedDevice.hardware_info?.memory_free_gb ? `${selectedDevice.hardware_info.memory_free_gb} ГБ` : '—'} />
                    <InfoRow label="Видео" value={selectedDevice.hardware_info?.gpu} />
                    <InfoRow label="Диски" value={selectedDevice.hardware_info?.disks} />
                  </DetailsBlock>
                </SectionCard>
              </VStack>
            </Grid>
          ) : <Text color="rgba(255,255,255,0.62)">Выберите панель</Text>}
        </Box>
      </Grid>

      {card ? (
        <Grid templateColumns={{ base: '1fr', xl: '1fr 1fr' }} gap={3} maxW="100%">
          <Box border="1px solid rgba(255,255,255,0.18)" borderRadius="8px" p={3} overflowX="auto" bg="rgba(15,15,28,0.72)" maxH="430px" overflowY="auto">
            <HStack justify="space-between" mb={2} align="flex-start" wrap="wrap" gap={2}>
              <Heading size="sm" color="white">История метрик</Heading>
              <Wrap spacing={3} justify="flex-end">
                {METRIC_OPTIONS.map(item => (
                  <WrapItem key={item.key}>
                    <Checkbox
                      isChecked={metricKeys.includes(item.key)}
                      onChange={event => {
                        setMetricKeys(current => {
                          if (event.target.checked) return Array.from(new Set([...current, item.key]));
                          const next = current.filter(key => key !== item.key);
                          return next.length ? next : [item.key];
                        });
                      }}
                      colorScheme="purple"
                      color="rgba(255,255,255,0.84)"
                    >
                      {item.label}
                    </Checkbox>
                  </WrapItem>
                ))}
              </Wrap>
            </HStack>
            <MetricsChart telemetry={card.telemetry || []} metricKeys={metricKeys} />
            <DetailsBlock title="Таблица значений">
              {(card.telemetry || []).length ? <Table size="sm" variant="unstyled" mt={3}>
                <Thead><Tr><Th color="rgba(255,255,255,0.58)">Время</Th><Th color="rgba(255,255,255,0.58)">CPU</Th><Th color="rgba(255,255,255,0.58)">Темп.</Th><Th color="rgba(255,255,255,0.58)">Диск</Th><Th color="rgba(255,255,255,0.58)">ОЗУ</Th></Tr></Thead>
                <Tbody>{(card.telemetry || []).slice(-30).reverse().map((r, i) => <Tr key={i} borderTop="1px solid rgba(255,255,255,0.08)"><Td>{fmt(r.timestamp || r.created_at || r.time)}</Td><Td>{metric(telemetryValue(r, 'cpu_load'))}</Td><Td>{temp(telemetryValue(r, 'temperature'))}</Td><Td>{metric(telemetryValue(r, 'disk_usage'))}</Td><Td>{metric(telemetryValue(r, 'memory_usage'))}</Td></Tr>)}</Tbody>
              </Table> : <Text color="rgba(255,255,255,0.58)" fontSize="13px">Истории метрик пока нет.</Text>}
            </DetailsBlock>
          </Box>
          <Box border="1px solid rgba(255,255,255,0.18)" borderRadius="8px" p={3} overflowX="hidden" bg="rgba(15,15,28,0.72)" maxH="340px">
            <HStack justify="space-between" mb={3} align="flex-start" wrap="wrap" gap={2}>
              <Heading size="sm" color="white">Команды и ответы</Heading>
              <HStack spacing={2} wrap="wrap">
                <Button
                  size="xs"
                  bg={showCommandTechnical ? '#7A4CE0' : 'rgba(255,255,255,0.1)'}
                  color="white"
                  _hover={{ bg: showCommandTechnical ? '#8B5CF6' : 'rgba(255,255,255,0.16)' }}
                  onClick={() => setShowCommandTechnical(value => !value)}
                >
                  Технический ответ
                </Button>
                <Button
                  size="xs"
                  bg={showCommandScreenshots ? '#7A4CE0' : 'rgba(255,255,255,0.1)'}
                  color="white"
                  _hover={{ bg: showCommandScreenshots ? '#8B5CF6' : 'rgba(255,255,255,0.16)' }}
                  onClick={() => setShowCommandScreenshots(value => !value)}
                >
                  Показать полученный скриншот
                </Button>
                <Button size="xs" bg="rgba(255,255,255,0.1)" color="white" _hover={{ bg: 'rgba(255,255,255,0.16)' }} onClick={() => loadCommandHistory(selectedFabric)}>
                  Обновить
                </Button>
              </HStack>
            </HStack>
            <VStack align="stretch" spacing={3} maxH="360px" overflowY="auto">
              {commandHistory.length ? commandHistory.slice(0, 20).map((item, index) => (
                <Box key={item.id || index} border="1px solid rgba(255,255,255,0.12)" borderRadius="8px" p={3} bg="rgba(255,255,255,0.04)">
                  <HStack justify="space-between" mb={2}>
                    <Text color="white" fontWeight="700" fontSize="13px" noOfLines={1}>{item.label || item.type || 'Команда'}</Text>
                    <Badge colorScheme={commandColor(item)}>
                      {item.status || item.state || item.result_status || 'queued'}
                    </Badge>
                  </HStack>
                  <Text color="rgba(255,255,255,0.55)" fontSize="12px" mb={2}>
                    {fmt(item.created_at || item.timestamp || item.started_at)}
                    {item.finished_at ? ` · завершено ${fmt(item.finished_at)}` : ''}
                    {item.exit_code !== null && item.exit_code !== undefined ? ` · код ${item.exit_code}` : ''}
                  </Text>
                  <Text color="rgba(255,255,255,0.76)" fontSize="12px" whiteSpace="pre-wrap" noOfLines={3}>{humanCommandOutput(item)}</Text>
                  {showCommandTechnical ? (
                    <Box as="pre" whiteSpace="pre-wrap" color="rgba(255,255,255,0.78)" fontSize="12px" maxH="180px" overflowY="auto" m={0}>
                      {commandOutput(item).slice(0, 5000)}
                    </Box>
                  ) : null}
                  {showCommandScreenshots && (item.type === 'screenshot' || item.label === 'Сделать скриншот') && latestShot ? (
                    <Box mt={2}>
                      <AuthorizedImage
                        url={latestShot}
                        token={pageStore.token}
                        h="180px"
                        w="120px"
                        objectFit="cover"
                        borderRadius="8px"
                        border="1px solid rgba(255,255,255,0.14)"
                        cursor="zoom-in"
                        onClick={event => setPreviewSrc(event.currentTarget.src)}
                      />
                    </Box>
                  ) : null}
                </Box>
              )) : (
                <Text color="rgba(255,255,255,0.58)" fontSize="13px">Истории команд пока нет.</Text>
              )}
            </VStack>
          </Box>
          <Box border="1px solid rgba(255,255,255,0.18)" borderRadius="8px" p={3} bg="rgba(15,15,28,0.72)" maxH="360px" overflowY="auto">
            <HStack justify="space-between" mb={2} align="flex-start" wrap="wrap" gap={2}>
              <Heading size="sm" color="white">История скриншотов</Heading>
              <HStack spacing={2} wrap="wrap">
                <Badge bg="rgba(122,76,224,0.24)" color="#ddd6fe">{filteredScreenshots.length}</Badge>
              </HStack>
            </HStack>
            {latestScreenshotCommand ? (
              <Alert status={commandFailed(latestScreenshotCommand) ? 'error' : commandFinished(latestScreenshotCommand) ? 'success' : 'info'} mb={3} borderRadius="8px" bg="rgba(255,255,255,0.08)" color="white">
                <AlertIcon />
                Последняя команда скриншота: {latestScreenshotCommand.status || 'в очереди'} · {fmt(latestScreenshotCommand.finished_at || latestScreenshotCommand.created_at)}
              </Alert>
            ) : null}
            {filteredScreenshots.length ? (
              <SimpleGrid columns={{ base: 2, md: 5, xl: 6 }} spacing={2} maxH="300px" overflowY="auto">
                {filteredScreenshots.slice(0, 80).map((item, index) => (
                  <Box key={item.id} cursor="zoom-in">
                    <AuthorizedImage
                      url={item.url}
                      token={pageStore.token}
                      h="112px"
                      w="100%"
                      objectFit="cover"
                      borderRadius="8px"
                      border="1px solid rgba(255,255,255,0.14)"
                      onClick={() => openScreenshotModal(index)}
                    />
                    <Text mt={1} color="rgba(255,255,255,0.58)" fontSize="11px">{fmt(item.timestamp || item.created_at)}</Text>
                  </Box>
                ))}
              </SimpleGrid>
            ) : (
              <Text color="rgba(255,255,255,0.58)" fontSize="13px">История скриншотов пока пустая.</Text>
            )}
          </Box>
          <Box border="1px solid rgba(255,255,255,0.18)" borderRadius="8px" p={3} bg="rgba(15,15,28,0.72)" maxH="360px" overflowY="auto">
            <HStack justify="space-between" mb={3} align="center" wrap="wrap" gap={2}>
              <Heading size="sm" color="white">История проверок</Heading>
              <Badge bg="rgba(122,76,224,0.24)" color="#ddd6fe">{audit.length}</Badge>
            </HStack>
            {audit.length ? (
              <VStack align="stretch" spacing="8px">
                {audit.map(item => <AuditHistoryCard key={item.id} item={item} />)}
              </VStack>
            ) : (
              <Text color="rgba(255,255,255,0.58)" fontSize="13px">Истории проверок пока нет.</Text>
            )}
          </Box>
        </Grid>
      ) : null}

      <Modal isOpen={screenshotModalIndex !== null} onClose={closeScreenshotModal} size="5xl" isCentered>
        <ModalOverlay bg="rgba(0,0,0,0.72)" />
        <ModalContent bg="#10101d" border="1px solid rgba(255,255,255,0.18)">
          <ModalCloseButton
            aria-label="Закрыть просмотр скриншота"
            color="white"
            bg="rgba(0,0,0,0.58)"
            zIndex={10}
            top="10px"
            right="10px"
            onClick={closeScreenshotModal}
            _hover={{ bg: 'rgba(255,255,255,0.18)' }}
          />
          <ModalBody p={5} position="relative">
            <HStack justify="space-between" mb={3} pr={8}>
              <Text color="white" fontWeight="700">
                Скриншот {(screenshotModalIndex ?? 0) + 1} из {filteredScreenshots.length}
              </Text>
              <Text color="rgba(255,255,255,0.58)" fontSize="13px">
                {fmt(filteredScreenshots[screenshotModalIndex]?.timestamp || filteredScreenshots[screenshotModalIndex]?.created_at)}
              </Text>
            </HStack>
            <HStack justify="space-between" align="center" gap={3}>
              <Button
                minW="44px"
                h="44px"
                bg="rgba(255,255,255,0.1)"
                color="white"
                _hover={{ bg: 'rgba(255,255,255,0.16)' }}
                onClick={() => setScreenshotModalIndex(index => Math.max(0, (index || 0) - 1))}
                isDisabled={!screenshotModalIndex}
              >
                ←
              </Button>
              <AuthorizedImage
                url={filteredScreenshots[screenshotModalIndex]?.url}
                token={pageStore.token}
                maxH="78vh"
                maxW="100%"
                objectFit="contain"
                borderRadius="8px"
                bg="rgba(0,0,0,0.18)"
              />
              <Button
                minW="44px"
                h="44px"
                bg="rgba(255,255,255,0.1)"
                color="white"
                _hover={{ bg: 'rgba(255,255,255,0.16)' }}
                onClick={() => setScreenshotModalIndex(index => Math.min(filteredScreenshots.length - 1, (index || 0) + 1))}
                isDisabled={screenshotModalIndex === null || screenshotModalIndex >= filteredScreenshots.length - 1}
              >
                →
              </Button>
            </HStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!previewSrc} onClose={() => setPreviewSrc('')} size="5xl" isCentered>
        <ModalOverlay bg="rgba(0,0,0,0.72)" />
        <ModalContent bg="#10101d" border="1px solid rgba(255,255,255,0.18)">
          <ModalCloseButton
            aria-label="Закрыть просмотр скриншота"
            color="white"
            bg="rgba(0,0,0,0.58)"
            zIndex={10}
            top="10px"
            right="10px"
            onClick={() => setPreviewSrc('')}
            _hover={{ bg: 'rgba(255,255,255,0.18)' }}
          />
          <ModalBody p={5}>
            {previewSrc && previewSrc !== 'pending' ? <Image src={previewSrc} maxH="82vh" mx="auto" objectFit="contain" /> : <Spinner color="white" />}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!themePreview} onClose={() => setThemePreview(null)} size="lg" isCentered>
        <ModalOverlay bg="rgba(0,0,0,0.72)" />
        <ModalContent bg="#10101d" border="1px solid rgba(255,255,255,0.18)">
          <ModalCloseButton color="white" />
          <ModalBody p={5}>
            <Heading size="sm" mb={3} color="white">
              {themePreview?.title || themePreview?.name || 'Тема периода'} · {themePreview?.start || '—'} - {themePreview?.end || '—'}
            </Heading>
            <SceneMiniPreview theme={themePreview?.theme} height="520px" />
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default MonitoringPage;
